<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->


<template>
<div id="app" class="wrapper">
  <notifications position="top center" width="30%" :max="5">
    <template #body="props">
      <div class="notification vue-notification" :class="props.item.type">
        <button class="delete" @click="props.close"></button>
        <strong class="notification-title">
          {{props.item.title}}
        </strong>
        <div class="notification-content" v-html="props.item.text"></div>
      </div>
    </template>
  </notifications>

  <template v-if="!loading">
    <div class="box error" v-if="communicationError">
      <h2>
        {{$t('communication-error')}}
      </h2>
      {{$t('core-cannot-be-reached')}}
    </div>

    <login v-else-if="!currentUser" />

    <template v-else>
      <!-- Case 페이지: Floating sidebar with full-screen content -->
      <div v-if="isViewerPage" style="position: relative; width: 100%; height: 100vh;">
        <SidebarComponent />
        <div style="width: 100%; height: 100%;">
          <div class="bottom body-content">
            <keep-alive include="cytomine-storage">
              <router-view :key="$route.fullPath" v-if="currentUser" />
            </keep-alive>
          </div>
        </div>
      </div>
      
      <!-- 다른 페이지: Traditional flexbox layout -->
      <div v-else style="display: flex;">
        <SidebarComponent />
        <div style="flex: 1; min-width: 0;">
          <div class="bottom body-content">
            <keep-alive include="cytomine-storage">
              <router-view :key="$route.fullPath" v-if="currentUser" />
            </keep-alive>
          </div>
        </div>
      </div>

      <div class="hidden" v-shortkey.once="openHotkeysModalShortcut" @shortkey="openHotkeysModal"></div>
    </template>
  </template>
</div>
</template>

<script>
// import axios from 'axios';
import {get} from '@/utils/store-helpers';
import {changeLanguageMixin} from '@/lang.js';

// import CytomineNavbar from './components/navbar/CytomineNavbar.vue';
import SidebarComponent from './components/sidebar/SidebarComponent.vue';
import Login from './components/user/Login.vue';
import shortcuts from '@/utils/shortcuts.js';
import HotkeysModal from '@/components/navbar/HotkeysModal';

import {Cytomine} from 'cytomine-client';

import constants from '@/utils/constants.js';
import ifvisible from 'ifvisible';
ifvisible.setIdleDuration(constants.IDLE_DURATION);

export default {
  name: 'app',
  components: {Login, SidebarComponent},
  mixins: [changeLanguageMixin],
  data() {
    return {
      communicationError: false,
      loading: true,
      timeout: null,
      hotkeysModal: null,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    project: get('currentProject/project'),
    serverIp: get('serverInfo/serverIp'),
    isViewerPage() {
      // Check if current route is a case/viewer page
      const path = this.$route.hash ? this.$route.hash.slice(1) : this.$route.path;
      return path.startsWith('/case') || path.startsWith('/project');
    },
    openHotkeysModalShortcut() {
      return shortcuts['general-shortcuts-modal'];
    }
  },
  methods: {
    // required to use programmatic modal for correct display in IE11
    openHotkeysModal() {
      if (!this.hotkeysModal) {
        this.hotkeysModal = this.$buefy.modal.open({
          parent: this,
          component: HotkeysModal,
          hasModalCard: true,
          onCancel: () => this.hotkeysModal = null,
        });
      }
    },
    async loginWithToken() {
      try {
        let {shortTermToken} = await Cytomine.instance.loginWithToken(this.$route.query.username, this.$route.query.token);
        this.$store.commit('currentUser/setShortTermToken', shortTermToken);

        await this.fetchUser();
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('invalid-token')});
      }
    },
    async getServerIP() {
      try {
        let response = await fetch('/host-ip');
        let ip = await response.text();
        
        if (this.serverIp != ip) {
          this.$store.commit('serverInfo/setServerIp', ip);
          return ip.trim();
        }
        else {
          return this.serverIp;
        }
      }
      catch (error) {
        console.error('Failed to get server IP:', error);
        return null;
      }
    },
    async ping() {
      if(!ifvisible.now()){
        return; // window not visible or inactive user => stop pinging
      }
      try {
        let {authenticated, shortTermToken} = await Cytomine.instance.ping(this.project ? this.project.id : null);

        this.$store.commit('currentUser/setShortTermToken', shortTermToken);
        await this.getServerIP();

        if(this.currentUser && !authenticated) {
          await this.$store.dispatch('logout');
        }
        if(!this.currentUser && authenticated) {
          await this.fetchUser();
        }
        this.communicationError = false;
      }
      catch(error) {
        console.log(error);
        if (error.toString().indexOf('401')!==-1) {
          this.$notify({type: 'error', text: this.$t('unauthorized-request-detected')});
          this.communicationError = false;
          Cytomine.instance.logout();
          this.$store.dispatch('logout');
          this.changeLanguage();
          this.$router.replace('/').catch(() => {});
        }
        else if (error.toString().indexOf('403')!==-1) {
          this.$notify({type: 'error', text: this.$t('deactivate-account')});
          Cytomine.instance.logout();
          this.$store.dispatch('logout');
          this.changeLanguage();
          this.$router.replace('/').catch(() => {});
        }
        else {
          this.communicationError = true;
        }
      }

      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.ping, constants.PING_INTERVAL);
    },
    async fetchUser() {
      await this.$store.dispatch('currentUser/fetchUser');
      if(this.currentUser) {
        this.changeLanguage(this.currentUser.language);
      }
    }
  },
  async created() {
    let settings;
    // await axios
    //   .get('configuration.json')
    //   .then(response => (settings = response.data));

    for (let i in settings) {
      if (Object.prototype.hasOwnProperty.call(constants, i)
        || i.includes('_NAMESPACE') || i.includes('_VERSION') || i.includes('_ENABLED')) {
        constants[i] = settings[i];
      }
    }
    Object.freeze(constants);

    new Cytomine(window.location.origin);

    if(this.$route.query.token && this.$route.query.username) {
      await this.loginWithToken();
    }
    await this.ping();
    this.loading = false;
    ifvisible.on('wakeup', this.ping);
  }
};
</script>

<style lang="scss">
@import '@/assets/styles/main.scss';

@font-face {
  font-family: 'cytomine';
  src: url('assets/fonts/cytomine-font.woff') format('woff');
}

@font-face {
  font-family: 'pretendard-medium';
  src: url('assets/fonts/Pretendard-Medium.woff') format('woff');
}

/* Inter Regular */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  src: url('@/assets/fonts/Inter-Regular.woff2') format('woff2');
}

/* Inter Medium */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  src: url('@/assets/fonts/Inter-Medium.woff2') format('woff2');
}

/* Inter SemiBold */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  src: url('@/assets/fonts/Inter-SemiBold.woff2') format('woff2');
}

/* Inter Bold */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 700;
  src: url('@/assets/fonts/Inter-Bold.woff2') format('woff2');
}

/* Inter Extra Bold */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 800;
  src: url('@/assets/fonts/Inter-ExtraBold.woff2') format('woff2');
}

/* Inter Thin */
@font-face {
  font-family: 'Inter';
  font-style: normal;
  font-weight: 100;
  src: url('@/assets/fonts/Inter-Thin.woff2') format('woff2');
}

html, body {
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

body {
  font-family: 'Inter', 'pretendard-medium', sans-serif;
  color: #333;
  background: #ffffff;
}

.wrapper {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  background: #ffffff;
}

.box.error {
  max-width: 600px;
  margin: auto;
  margin-top: 3rem;
}

.notifications {
  margin-top: 1em;
}

.notification.info {
  background: #77b1ea;
}

.bottom {
  flex: 1;
  overflow-y: auto;
  /* position: relative; */
}

.body-content {
  height: 100vh;
}

h1 {
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  font-size: 1.25rem;
  text-align: center;
  padding: 0.9rem;
}

h2 {
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  margin-bottom: 1rem;
}

.in-project {
  color: grey;
  font-size: 0.8em;
}

.input[readonly] {
  background-color: whitesmoke;
}

strong, .label {
  font-weight: 600 !important;
}

.content-wrapper {
  padding: 1.5% 2.5%;
  position: relative;
  min-height: 100%;
}

/* Filters */

.filters {
  background: #f8f8f8;
  margin-top: 1.2rem;
  border-radius: 10px;
  padding: 1rem;
}

.filter-label {
  text-transform: uppercase;
  font-size: 0.8em;
  margin-bottom: 0.5em;
  margin-left: 1em;
}

.filter-label .no-uppercase {
  text-transform: none;
}

.nb-active-filters {
  display: inline-block;
  background: $primary;
  color: $primary-invert;
  min-width: 1.25rem;
  height: 1.25rem;
  font-weight: 600;
  border-radius: 0.625rem;
  margin-left: 0.5em;
  font-size: 0.9em;
  line-height: 1.25em;
  padding: 0 0.25em;
  position: absolute;
  top: -0.3em;
  right: -0.6em;
}

/* For correct display of svg images on IE > 10 */
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  img[src$=".svg"] {
    width: 100%;
  }
}

.display-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.display-center-row {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.display-center-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.flex-row-start {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.flex-row-between-center {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.flex-column-start {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

.flex-column-start-center {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

</style>
